import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleClick, true);
    // Capture submit so keyboard/alternative triggers also close the drawer.
    this.addEventListener('submit', this.#handleSubmit, true);

    // When the user returns via the browser back/forward cache, force a refresh
    // so the cart drawer always reflects the latest server cart state.
    if (!window.__cartBackRefreshBound) {
      window.addEventListener(
        'pageshow',
        (event) => {
          const navEntry = performance.getEntriesByType('navigation')[0];
          const isBackForward = navEntry?.type === 'back_forward' || navEntry?.type === 'backforward';
          if (event.persisted || isBackForward) {
            window.location.reload();
          }
        },
        { once: false }
      );
      window.__cartBackRefreshBound = true;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleClick, true);
    this.removeEventListener('submit', this.#handleSubmit, true);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Close the drawer when the checkout CTA inside it is clicked so the Razorpay
   * modal or the following page load isn't obscured by the drawer overlay.
   */
  #handleClick = (event) => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    
    // Handle Proxy Button
    const proxyButton = path.find(el => el instanceof Element && el.classList.contains('cart__checkout-button-proxy'));
    if (proxyButton) {
      this.closeDialog({ skipAnimation: true });
      
      // Trigger the real checkout button
      const realButton = document.getElementById('checkout');
      if (realButton) {
        realButton.click();
      }
      return;
    }

    const isCheckoutClick = path.some(
      (el) =>
        el instanceof Element &&
        (el.id === 'checkout' ||
          el.name === 'checkout' ||
          el.hasAttribute('data-close-drawer-checkout') ||
          el.classList.contains('cart__checkout-button') ||
          el.closest('.cart__ctas'))
    );

    if (isCheckoutClick && path.includes(this)) {
      this.closeDialog({ skipAnimation: true });
    }
  };

  #handleSubmit = (event) => {
    // Close drawer on any form submission inside (covers keyboard submit + alt payments).
    if (this.contains(event.target)) {
      this.closeDialog({ skipAnimation: true });
    }
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
